import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Map as MapIcon, Globe as GlobeIcon, Activity, ShieldAlert,
  BarChart3, Menu, X, RefreshCw, Sparkles, Bell, BellOff, Play, Pause, Newspaper
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";

import Map2D from "../components/Map2D";
import Globe3D from "../components/Globe3D";
import QuakeFeed from "../components/QuakeFeed";
import Filters from "../components/Filters";
import LayersMenu from "../components/LayersMenu";
import TsunamiBanner from "../components/TsunamiBanner";
import NewsTicker from "../components/NewsTicker";
import MitigationModal from "../components/MitigationModal";
import SocialCard from "../components/SocialCard";
import CrossSection from "../components/CrossSection";
import AdminPanel from "../components/AdminPanel";
import BrandLogo from "../components/BrandLogo";
import PressRelease from "../components/PressRelease";

import { fetchUSGS, fetchBMKG, fetchFires, fetchGeoJSON, fetchPVMBG, REGION_PRESETS, VOLCANOES as BASE_VOLCANOES } from "../lib/api";
import { loadConfig, getAccent } from "../lib/config";
import { loadAlertSettings, saveAlertSettings, requestNotificationPermission, evaluateAlerts } from "../lib/alerts";

const ALARM_URL = "data:audio/wav;base64,UklGRnQCAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YVACAAA=";

function playAlarm() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.05);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.9);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.9);
  } catch {}
}

export default function Pantau() {
  const location = useLocation();
  const navigate = useNavigate();

  const [cfg, setCfg] = useState(loadConfig);
  const accent = getAccent(cfg);

  const [view, setView] = useState("2d"); // 2d | 3d
  const [feed, setFeed] = useState(cfg.defaultFeed);
  const [region, setRegion] = useState(cfg.defaultRegion);
  const [layers, setLayers] = useState(cfg.defaultLayers);
  const [filters, setFilters] = useState({ minMag: 0, maxDepth: 700, search: "", fireRegion: "southeast_asia", minFrp: 0 });
  const [sound, setSound] = useState(!!cfg.soundAlarm);

  const [quakes, setQuakes] = useState([]);
  const [wildfires, setWildfires] = useState([]);
  const [firesRaw, setFiresRaw] = useState([]);
  const [volcanoes, setVolcanoes] = useState(BASE_VOLCANOES);
  const [geoPlates, setGeoPlates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Historical playback (0 = all last 24h; 1..7 = specific day 1..7 days back)
  const [playbackDay, setPlaybackDay] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Push alerts settings
  const [alerts, setAlerts] = useState(loadAlertSettings);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const [selected, setSelected] = useState(null);
  const [focusQuake, setFocusQuake] = useState(null);
  const [tsunami, setTsunami] = useState(null);
  const [showMitigation, setShowMitigation] = useState(false);
  const [showCross, setShowCross] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [cardQuake, setCardQuake] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [pr, setPr] = useState({ open: false, kind: null, data: null });

  const knownIdsRef = useRef(new Set());

  // Admin panel entry conditions
  useEffect(() => {
    const check = () => {
      const params = new URLSearchParams(location.search);
      if (
        location.pathname === cfg.adminSecretPath ||
        params.get("admin") === "1" ||
        window.location.hash === "#admin"
      ) {
        setShowAdmin(true);
      }
    };
    check();
    const key = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "A" || e.key === "a")) {
        e.preventDefault();
        setShowAdmin(true);
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [location, cfg.adminSecretPath]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    let list = [];
    try {
      if (feed === "combined") {
        const [bmkg, usgs] = await Promise.all([fetchBMKG("terkini"), fetchUSGS("day_45")]);
        list = [...(bmkg || []), ...usgs];
      } else if (feed === "bmkg") {
        const bmkg = await fetchBMKG("terkini");
        if (!bmkg) {
          toast.warning("BMKG tidak tersedia — fallback ke USGS");
          list = await fetchUSGS("day_45");
        } else list = bmkg;
      } else if (feed.startsWith("usgs:")) {
        list = await fetchUSGS(feed.slice(5));
      }
    } catch (e) {
      toast.error("Gagal memuat feed: " + e.message);
    }
    // Dedup by lat/lng/time within 60s
    const seen = new Map();
    list.forEach((q) => {
      const key = `${q.latitude.toFixed(2)}-${q.longitude.toFixed(2)}-${Math.round(q.time / 60000)}`;
      if (!seen.has(key)) seen.set(key, q);
    });
    const dedup = [...seen.values()].sort((a, b) => b.time - a.time);

    // Alarm for new M>=5 quakes
    if (sound) {
      dedup.forEach((q) => {
        if (q.magnitude >= 5.0 && !knownIdsRef.current.has(q.id)) {
          playAlarm();
        }
      });
    }
    dedup.forEach((q) => knownIdsRef.current.add(q.id));

    setQuakes(dedup);
    setLastUpdate(Date.now());
    setLoading(false);

    // Detect tsunami
    const t = dedup.find((q) => q.tsunami && (typeof q.tsunami === "boolean" ? q.magnitude >= 6.5 : true));
    if (t && (!tsunami || tsunami.id !== t.id)) setTsunami(t);
  }, [feed, sound, tsunami]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(fetchData, (cfg.refreshInterval || 60) * 1000);
    return () => clearInterval(interval);
  }, [fetchData, cfg.refreshInterval]);

  // Load supplementary layers — FIRMS 24h by default, upgrade to 7d only when playback is active
  const [firmsDays, setFirmsDays] = useState("24h");
  useEffect(() => {
    if (layers.wildfires) {
      fetchFires(filters.fireRegion, filters.minFrp, firmsDays).then(setFiresRaw);
    } else {
      setFiresRaw([]);
    }
  }, [layers.wildfires, filters.fireRegion, filters.minFrp, firmsDays]);

  // Upgrade to 7d automatically when the user starts scrubbing/playing
  useEffect(() => {
    if ((playing || playbackDay > 0) && firmsDays !== "7d") setFirmsDays("7d");
  }, [playing, playbackDay, firmsDays]);

  // Filter fires by playbackDay: 0 = show all last 7d; N>0 = only that day (N days ago window)
  useEffect(() => {
    if (!firesRaw.length) { setWildfires([]); return; }
    if (playbackDay === 0) { setWildfires(firesRaw); return; }
    const now = Date.now();
    const dayMs = 24 * 3600 * 1000;
    const start = now - playbackDay * dayMs;
    const end = start + dayMs;
    setWildfires(firesRaw.filter((f) => {
      // FIRMS date format "YYYY-MM-DD HHMM"; EONET is ISO
      let t;
      if (f.source === "FIRMS" && f.date) {
        const [d, hm] = f.date.split(" ");
        const [y, m, dd] = d.split("-").map(Number);
        const hh = hm ? parseInt(hm.slice(0, 2)) : 0;
        const mm = hm ? parseInt(hm.slice(2)) : 0;
        t = Date.UTC(y, (m || 1) - 1, dd || 1, hh, mm);
      } else {
        t = new Date(f.date).getTime();
      }
      return t >= start && t < end;
    }));
  }, [firesRaw, playbackDay]);

  // Playback auto-advance
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setPlaybackDay((d) => (d >= 7 ? 0 : d + 1));
    }, 1500);
    return () => clearInterval(id);
  }, [playing]);

  useEffect(() => {
    if (layers.plates && !geoPlates) fetchGeoJSON("plates").then(setGeoPlates);
  }, [layers.plates, geoPlates]);

  // PVMBG live sync — merge official alertLevels into curated list
  useEffect(() => {
    fetchPVMBG().then((map) => {
      if (!map || !Object.keys(map).length) return;
      setVolcanoes((cur) => cur.map((v) => {
        const live = map[v.name.toLowerCase()];
        return live ? { ...v, alertLevel: live.alertLevel, alertLabel: live.label, alertUpdated: live.updated, alertLive: true } : v;
      }));
    });
  }, []);

  // Push alerts evaluator — runs each time data updates
  useEffect(() => {
    evaluateAlerts({ settings: alerts, quakes, volcanoes, fires: wildfires });
  }, [alerts, quakes, volcanoes, wildfires]);

  // Filtered list
  const filtered = useMemo(() => {
    const s = filters.search.trim().toLowerCase();
    return quakes.filter((q) =>
      q.magnitude >= filters.minMag &&
      q.depth <= filters.maxDepth &&
      (!s || q.location.toLowerCase().includes(s))
    );
  }, [quakes, filters]);

  const activeRegion = REGION_PRESETS[region];

  const handleSelect = (q) => { setSelected(q); setFocusQuake(q); };
  const handleShare = (q) => { setCardQuake(q); setShowCard(true); };

  return (
    <div className="grain relative flex h-screen w-screen flex-col overflow-hidden bg-slate-950 text-slate-100" data-testid="pantau-app">
      <TsunamiBanner quake={tsunami} onDismiss={() => setTsunami(null)} />

      {/* Header */}
      <header className="relative z-30 flex h-16 shrink-0 items-center gap-3 border-b border-slate-800/80 bg-slate-950/80 px-3 backdrop-blur-xl sm:px-5">
        <button className="rounded-md p-2 hover:bg-slate-900 lg:hidden" onClick={() => setDrawer(true)} data-testid="menu-btn">
          <Menu className="h-5 w-5" />
        </button>
        <BrandLogo cfg={cfg} accent={accent} />
        <div className="hidden min-w-0 sm:block">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-lg font-bold tracking-tight text-slate-50">
              {cfg.siteName}{cfg.siteSuffix && <span className="text-slate-400"> · {cfg.siteSuffix}</span>}
            </h1>
            <span className="flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-red-400">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75"></span>
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500"></span>
              </span>
              LIVE
            </span>
          </div>
          <div className="truncate text-[11px] text-slate-500">{cfg.siteSubtitle}</div>
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          <Tabs value={view} onValueChange={setView} className="hidden sm:block">
            <TabsList className="border border-slate-800 bg-slate-900" data-testid="view-tabs">
              <TabsTrigger value="2d" className="data-[state=active]:bg-slate-800" data-testid="view-2d"><MapIcon className="mr-1 h-3.5 w-3.5" />2D</TabsTrigger>
              <TabsTrigger value="3d" className="data-[state=active]:bg-slate-800" data-testid="view-3d"><GlobeIcon className="mr-1 h-3.5 w-3.5" />3D</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="hidden lg:block">
            <LayersMenu layers={layers} setLayers={setLayers} filters={filters} setFilters={setFilters} />
          </div>
          <Button size="sm" variant="ghost" onClick={fetchData} disabled={loading} className="hover:bg-slate-900" data-testid="refresh-btn" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowCross(true)} className="hover:bg-slate-900" data-testid="cross-section-btn" title="Cross-Section">
            <BarChart3 className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={async () => {
              if (alerts.enabled) {
                const next = { ...alerts, enabled: false };
                setAlerts(next); saveAlertSettings(next);
                toast.info("Push alert dimatikan");
              } else {
                const p = await requestNotificationPermission();
                setNotifPerm(p);
                if (p === "granted") {
                  const next = { ...alerts, enabled: true };
                  setAlerts(next); saveAlertSettings(next);
                  toast.success("Push alert aktif (M≥6, Awas, FRP≥100 MW)");
                } else if (p === "denied") {
                  toast.error("Izin notifikasi ditolak. Aktifkan di setelan browser.");
                } else if (p === "unsupported") {
                  toast.error("Browser tidak mendukung Notification API.");
                }
              }
            }} className="hover:bg-slate-900" data-testid="alerts-btn" title="Push Alerts">
            {alerts.enabled ? <Bell className="h-4 w-4 text-cyan-400" /> : <BellOff className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowMitigation(true)} className="hover:bg-slate-900" data-testid="mitigation-btn" title="Mitigasi">
            <ShieldAlert className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="relative flex flex-1 overflow-hidden pb-8">
        {/* Left sidebar */}
        <aside className="hidden w-80 shrink-0 border-r border-slate-800/80 bg-slate-950/60 backdrop-blur-xl lg:flex lg:flex-col">
          <div className="border-b border-slate-800">
            <Filters
              filters={filters} setFilters={setFilters}
              layers={layers} setLayers={setLayers}
              feed={feed} setFeed={setFeed}
              region={region} setRegion={setRegion}
              sound={sound} setSound={setSound}
            />
          </div>
          <div className="min-h-0 flex-1">
            <QuakeFeed quakes={filtered} onSelect={handleSelect} onGenerateCard={handleShare} activeId={selected?.id} />
          </div>
          {lastUpdate && (
            <div className="border-t border-slate-800 px-3 py-1.5 font-mono text-[10px] text-slate-500">
              Last update: {new Date(lastUpdate).toLocaleTimeString("id-ID")} · Auto-refresh {cfg.refreshInterval || 60}s
            </div>
          )}
        </aside>

        {/* Map area */}
        <section className="relative min-w-0 flex-1">
          {view === "2d" && (
            <Map2D quakes={filtered} region={activeRegion} layers={layers} wildfires={wildfires} geoPlates={geoPlates} focusQuake={focusQuake} onSelect={handleSelect} accent={accent} volcanoes={volcanoes} onPressRelease={(kind, data) => setPr({ open: true, kind, data })} />
          )}
          {view === "3d" && (
            <Globe3D quakes={filtered} region={activeRegion} layers={layers} wildfires={wildfires} geoPlates={geoPlates} focusQuake={focusQuake} accent={accent} volcanoes={volcanoes} />
          )}

          {/* Floating stats */}
          <div className="pointer-events-none absolute right-3 top-3 z-10 space-y-2">
            <div className="pointer-events-auto rounded-xl border border-slate-800/80 bg-slate-950/80 px-4 py-2.5 backdrop-blur-xl">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Events (filtered)</div>
              <div className="font-mono text-2xl font-bold text-cyan-400" data-testid="stats-count">{filtered.length}</div>
            </div>
            {selected && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="pointer-events-auto w-72 rounded-xl border border-slate-800/80 bg-slate-950/85 p-3 backdrop-blur-xl"
                data-testid="selected-panel"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Selected Event · {selected.source}</div>
                    <div className="font-mono text-3xl font-bold" style={{ color: selected.depth < 50 ? "#ef4444" : selected.depth <= 300 ? "#f97316" : "#10b981" }}>M {selected.magnitude?.toFixed(1)}</div>
                  </div>
                  <button className="rounded-md p-1 hover:bg-slate-900" onClick={() => setSelected(null)}><X className="h-3.5 w-3.5" /></button>
                </div>
                <div className="mt-2 text-sm text-slate-100">{selected.location}</div>
                <div className="mt-1 space-y-0.5 font-mono text-[11px] text-slate-400">
                  <div>Depth: {selected.depth?.toFixed(1)} km</div>
                  <div>Lat/Lng: {selected.latitude?.toFixed(3)}, {selected.longitude?.toFixed(3)}</div>
                  <div>{new Date(selected.time).toLocaleString("id-ID")}</div>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => handleShare(selected)} className="flex-1 bg-cyan-500 text-slate-950 hover:bg-cyan-400" data-testid="share-selected"><Sparkles className="mr-1 h-3.5 w-3.5" /> Card</Button>
                  <Button size="sm" variant="outline" onClick={() => setPr({ open: true, kind: "quake", data: selected })} className="flex-1 border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800" data-testid="press-release-selected"><Newspaper className="mr-1 h-3.5 w-3.5" /> Naskah</Button>
                </div>
              </motion.div>
            )}
          </div>

          {/* Historical playback bar (only when wildfires layer on) */}
          {layers.wildfires && (
            <div className="pointer-events-auto absolute bottom-14 left-1/2 z-10 -translate-x-1/2 rounded-xl border border-slate-800/80 bg-slate-950/85 px-3 py-2 backdrop-blur-xl" data-testid="playback-bar">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setPlaying((p) => !p)}
                  className="rounded-md bg-slate-900 p-1.5 hover:bg-slate-800"
                  data-testid="playback-toggle"
                  title={playing ? "Pause" : "Play"}
                >
                  {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                </button>
                <div className="w-52 sm:w-72">
                  <div className="mb-0.5 flex items-center justify-between text-[10px] font-mono text-slate-400">
                    <span>FIRMS Playback</span>
                    <span className="text-cyan-400">
                      {playbackDay === 0 ? "All 7 days" : `${playbackDay} hari lalu · ${wildfires.length} titik`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={7}
                    step={1}
                    value={playbackDay}
                    onChange={(e) => { setPlaying(false); setPlaybackDay(parseInt(e.target.value)); }}
                    className="w-full accent-cyan-500"
                    data-testid="playback-slider"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Depth legend */}
          <div className="pointer-events-none absolute bottom-14 left-3 z-10">
            <div className="rounded-xl border border-slate-800/80 bg-slate-950/80 px-3 py-2 backdrop-blur-xl">
              <div className="mb-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">Depth Legend</div>
              <div className="flex items-center gap-3 text-[10px] font-mono">
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500"></span>&lt;50km</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-orange-500"></span>50-300</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500"></span>&gt;300</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <NewsTicker quakes={filtered.slice(0, 20)} />

      {/* Mobile drawer */}
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="left" className="z-[1100] w-80 border-slate-800 bg-slate-950 p-0 text-slate-100 sm:max-w-sm">
          <div className="max-h-[50vh] overflow-y-auto border-b border-slate-800">
            <Filters filters={filters} setFilters={setFilters} layers={layers} setLayers={setLayers} feed={feed} setFeed={setFeed} region={region} setRegion={setRegion} sound={sound} setSound={setSound} />
          </div>
          <div className="h-[50vh]"><QuakeFeed quakes={filtered} onSelect={(q) => { handleSelect(q); setDrawer(false); }} onGenerateCard={handleShare} activeId={selected?.id} /></div>
        </SheetContent>
      </Sheet>

      {/* Modals */}
      <MitigationModal open={showMitigation} onOpenChange={setShowMitigation} />
      <CrossSection open={showCross} onOpenChange={setShowCross} quakes={filtered} />
      <SocialCard open={showCard} onOpenChange={setShowCard} quake={cardQuake} cfg={cfg} />
      <PressRelease open={pr.open} onOpenChange={(v) => setPr((p) => ({ ...p, open: v }))} kind={pr.kind} data={pr.data} />
      <AdminPanel open={showAdmin} onOpenChange={(v) => { setShowAdmin(v); if (!v && location.pathname === cfg.adminSecretPath) navigate("/", { replace: true }); }} cfg={cfg} setCfg={setCfg} />
    </div>
  );
}
